import { useCallback, useEffect, useMemo, useState } from "react";
import { FileWithPath, useDropzone } from "react-dropzone";
import { io } from "socket.io-client";
import { AttributeType, DropFileType } from "types";
import usePreviewGenerator from "./usePreviewGenerator";
import { saveImage } from "tools/common";
import getDownload from "fetchers/getDownload";

function useGenerator() {
  const socket = useMemo(
    () => io("https://hassei-ki-backend.onrender.com/"),
    []
  );
  const [imageWidth, setImageWidth] = useState(0);
  const [imageHeight, setImageHeight] = useState(0);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreated, setIsCreated] = useState(false);
  const [uploadedAttributes, setUploadedAttributes] = useState(
    [] as AttributeType[]
  );
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showProgress, setShowProgress] = useState(false);
  const [messageReceived, setMessageReceived] = useState<string[]>([]);

  useEffect(() => {
    socket.on("generateProgress", (data) => {
      setMessageReceived((prev) => [...prev, data]);
    });

    return () => {
      socket.close();
    };
  }, [socket]);

  const resetState = () => {
    setMessageReceived([]);
    setUploadProgress(0);
    setIsPreviewLoading(false);
    setShowProgress(false);
  };

  const { generatePreview } = usePreviewGenerator({
    imageHeight,
    imageWidth,
    uploadedAttributes,
    setUploadProgress,
    setIsPreviewLoading,
    setShowProgress,
    setIsCreated,
    type: "make",
    resetState,
  });

  const onDrop = useCallback(
    (acceptedFiles: FileWithPath[]) => {
      const fileChecker = acceptedFiles.filter(
        (file) => file.path!.split("/").length !== 1
      );
      const renamed = fileChecker.map((file) => {
        const pathLength = file.path!.split("/").length;
        const hashtag = file.name.search("#") !== -1;
        const fileName = file.name.split(hashtag ? "#" : ".")[0];
        const rarity = hashtag ? file.name.split("#")[1].split(".")[0] : "1";
        const newFile = new File([file], `${fileName}#${rarity}.png`, {
          type: file.type,
        });
        return Object.assign(newFile, {
          folderName: file.path!.split("/")[pathLength > 3 ? 2 : 1],
          path: file.path,
          rarityWeight: rarity,
        });
      }) as DropFileType[];

      const grouppingObjects: { [name: string]: DropFileType[] } = groupBy(
        renamed,
        "folderName"
      );
      const newAttributes = [...uploadedAttributes];
      Object.keys(grouppingObjects).map((name) =>
        newAttributes.push({ name, files: grouppingObjects[name] })
      );
      newAttributes.sort((a, b) => a.name.localeCompare(b.name));

      setUploadedAttributes(newAttributes);
    },
    [uploadedAttributes]
  );

  function groupBy(arr: DropFileType[], property: string) {
    return arr.reduce(function (
      memo: { [name: string]: DropFileType[] },
      x: { [name: string]: any }
    ) {
      if (!memo[x[property]]) {
        memo[x[property]] = [];
      }
      memo[x[property]].push(x as DropFileType);
      return memo;
    },
    {});
  }

  const { getRootProps, getInputProps, fileRejections } = useDropzone({
    onDrop,
    noClick: false,
    accept: "image/png",
    maxSize: 1048576,
  });

  useEffect(() => {
    if (fileRejections.length) {
      // setShowFileReject(true);
    }
  }, [fileRejections]);

  useEffect(() => {
    if (uploadedAttributes.length) {
      const image = new Image();
      let url: string;
      image.onload = function () {
        setImageHeight(image.height);
        setImageWidth(image.width);
      };
      url = URL.createObjectURL(uploadedAttributes[0].files[0]);
      image.src = url;
    }
  }, [uploadedAttributes]);

  const getZip = async () => {
    setIsCreated(false);
    setIsPreviewLoading(true);
    setShowProgress(true);

    await getDownload()
      .then((res) => {
        setIsCreated(true);
        setIsPreviewLoading(false);
        resetState();
        saveImage(res.data, "zip", setIsCreated);
      })
      .catch((err) => {
        setIsCreated(true);
        resetState();
        setIsPreviewLoading(false);
      });
  };

  const uploadLabel = useMemo(() => {
    if (uploadProgress > 0 && Math.round(uploadProgress * 100) !== 100) {
      return `Upload Progress: ${Math.round(uploadProgress * 100)}%`;
    } else if (messageReceived.length >= 0) {
      return `Generating your image... ${
        messageReceived.length > 0
          ? `(${messageReceived[messageReceived.length - 1]})`
          : ""
      }`;
    }

    return "";
  }, [uploadProgress, messageReceived]);

  return {
    showProgress,
    isPreviewLoading,
    isCreated,
    uploadedAttributes,
    generatePreview,
    getZip,
    isModalOpen,
    setIsModalOpen,
    uploadLabel,
    getInputProps,
    getRootProps,
    setUploadedAttributes,
  };
}

export default useGenerator;
